import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { store } from '@/state/store';
import type { DocComment } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// CommentHistory (Fáze 3) — napojení komentářů na Zpět/Znovu.
//
// PROBLÉM: undo v TipTapu verzuje jen ProseMirror dokument (text, marky).
// Data komentářů žijí ve store, o kterém historie neví — bez tohoto pluginu
// by undo sundalo mark z textu, ale komentář by ve store zůstal (osiřelý).
//
// ŘEŠENÍ: plugin vede vlastní zásobník snapshotů (undo/redo stack), který
// se posouvá SPOLU s ProseMirror historií. Každá změna komentářů projde
// transakcí s meta `setComments` → snapshot se odloží do undo zásobníku.
// Když ProseMirror provede undo, plugin vyzvedne snapshot ze zásobníku
// a promítne ho do store; redo funguje zrcadlově.
//
// Proč vlastní zásobník: ProseMirror stav pluginu při undo automaticky
// nevrací — historie zná jen kroky nad dokumentem, ne nad naším stavem.
// ═══════════════════════════════════════════════════════════════

export const commentHistoryKey = new PluginKey<CommentHistoryState>('commentHistory');

/** Meta klíč pro transakci nesoucí nový stav komentářů. */
export const SET_COMMENTS_META = 'setComments';

interface CommentHistoryState {
  /** Aktuální stav komentářů. */
  comments: DocComment[];
  /** Snapshoty pro Zpět (poslední na konci). */
  undoStack: DocComment[][];
  /** Snapshoty pro Znovu (poslední na konci). */
  redoStack: DocComment[][];
}

/** Rozpozná transakci pocházející z undo/redo (prosemirror-history). */
function historyDirection(tr: any): 'undo' | 'redo' | null {
  const meta = tr.getMeta('history$');
  if (!meta) return null;
  // prosemirror-history posílá { redo: boolean } — true = jde o redo.
  return meta.redo ? 'redo' : 'undo';
}

export const CommentHistory = Extension.create({
  name: 'commentHistory',

  addProseMirrorPlugins() {
    return [
      new Plugin<CommentHistoryState>({
        key: commentHistoryKey,

        state: {
          init: () => ({
            comments: store.get().doc.comments || [],
            undoStack: [],
            redoStack: [],
          }),

          apply: (tr, prev) => {
            // 1) Nová změna komentářů → odlož současný stav do undo zásobníku
            //    a vyprázdni redo (jako u klasické historie).
            const next = tr.getMeta(SET_COMMENTS_META) as DocComment[] | undefined;
            if (next) {
              return {
                comments: next,
                undoStack: [...prev.undoStack, prev.comments],
                redoStack: [],
              };
            }

            // 2) Undo/redo z historie → posuň se po zásobníku.
            const dir = historyDirection(tr);
            if (dir === 'undo' && prev.undoStack.length) {
              const restored = prev.undoStack[prev.undoStack.length - 1];
              return {
                comments: restored,
                undoStack: prev.undoStack.slice(0, -1),
                redoStack: [...prev.redoStack, prev.comments],
              };
            }
            if (dir === 'redo' && prev.redoStack.length) {
              const restored = prev.redoStack[prev.redoStack.length - 1];
              return {
                comments: restored,
                undoStack: [...prev.undoStack, prev.comments],
                redoStack: prev.redoStack.slice(0, -1),
              };
            }

            return prev;
          },
        },

        view() {
          return {
            update: (view, prevState) => {
              const now = commentHistoryKey.getState(view.state);
              const before = commentHistoryKey.getState(prevState);
              if (!now || !before) return;
              // Stav se změnil (undo/redo) → promítni do store.
              if (now.comments !== before.comments) {
                const current = store.get().doc.comments || [];
                if (current !== now.comments) {
                  store.patchDoc({ comments: now.comments });
                }
              }
            },
          };
        },
      }),
    ];
  },
});
